/**
 * Code editor field for the page-builder inspector (ADR-0022 #2). A Monaco
 * editor (JS/JSX) with Tray's themes + `model` ambient types for IntelliSense,
 * and an **expand → modal** button so the author can write a real component in
 * a roomy editor instead of a cramped inspector textarea.
 *
 * Used for `x-tray-code` fields (the `custom_component` `code` prop). Monaco is
 * already in the bundle (CodeEditor + ExpressionField), so this adds no weight.
 */
import { loader } from "@monaco-editor/react";
import { Maximize2 } from "lucide-react";
import { useEffect, useState } from "react";

import { CodeEditor } from "@/components/canonical/CodeEditor";
import { Button } from "@/components/ui/button";
import { Dialog, DialogBody, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

import { Field } from "./inspectorKit";

/** Ambient TS so `model.` / `useTrayModel().` autocomplete inside the editor.
 *  Mirrors the runtime `useTrayModel()` shape (lib/customComponent.ts). */
const MODEL_DTS = `
declare const React: any;
interface TrayQueryResult { rows: any[]; total: number; data: any; }
interface TrayModel {
  /** Page variables ($page state). */
  vars: Record<string, any>;
  /** Page resources by name; each has .rows / .total / .data. */
  queries: Record<string, TrayQueryResult>;
  user: { id: string; email: string; full_name?: string | null } | null;
  role: { id: string; slug: string; name: string } | null;
  appSlug: string;
  pageSlug: string;
  /** Write a page variable (two-way binding back to the page). */
  setVar(name: string, value: any): void;
  /** Re-fetch the page's resources. */
  refresh(): void;
  navigate(to: string): void;
  toast(message: string): void;
}
/** The live two-way model passed as a prop AND available via the hook. */
declare const model: TrayModel;
declare function useTrayModel(): TrayModel;
/** React hooks are exposed as frame globals — usable without an import. */
declare function useState<T = any>(initial: T | (() => T)): [T, (v: T | ((p: T) => T)) => void];
declare function useEffect(fn: () => void | (() => void), deps?: any[]): void;
declare function useMemo<T = any>(fn: () => T, deps?: any[]): T;
declare function useCallback<T = any>(fn: T, deps?: any[]): T;
declare function useRef<T = any>(initial: T): { current: T };
declare function useReducer(reducer: any, initial: any): [any, (action: any) => void];
declare function useContext(ctx: any): any;
declare function useLayoutEffect(fn: () => void | (() => void), deps?: any[]): void;
declare function createElement(type: any, props?: any, ...children: any[]): any;
declare const Fragment: any;
`;

// Monaco's JavaScript worker flags JSX as a syntax error by default. Enable JSX
// once (global, harmless for plain-JS expression fields) so the editor doesn't
// red-squiggle every `<div>`.
let jsxConfigured = false;
function ensureJsxConfigured(): void {
  if (jsxConfigured) return;
  jsxConfigured = true;
  loader
    .init()
    .then((monaco) => {
      const js = monaco.languages.typescript.javascriptDefaults;
      js.setCompilerOptions({
        ...js.getCompilerOptions(),
        jsx: monaco.languages.typescript.JsxEmit.React,
        allowJs: true,
        allowNonTsExtensions: true,
      });
    })
    .catch(() => {
      /* editor still works without JSX leniency */
    });
}

const PLACEHOLDER =
  "function Component({ model }) {\n  return <div>{model.vars.title}</div>;\n}";

export function CodeField({
  label,
  hint,
  value,
  onChange,
  /** Ambient TS for IntelliSense. Defaults to the custom_component `model` shape;
   *  pass a different one (e.g. a JS-query `ctx.db` shape) to retarget hints. */
  ambientDts = MODEL_DTS,
  /** Greyed placeholder shown when empty. Defaults to the component skeleton. */
  placeholder = PLACEHOLDER,
  /** Monaco language. `typescript` (ADR-0054 app-logic `handler` body) runs real
   *  semantic type-checking against `ambientDts`; the default `javascript` is the
   *  lenient mode for raw bodies / custom-component JSX. */
  language = "javascript",
  /** Reports the count of TS ERROR markers (drives a save-block in the caller). */
  onDiagnostics,
  /** HERO mode: fill the parent (a flex column) instead of the fixed 200px inline
   *  box + label chrome — the code becomes the central surface (the query editor).
   *  The caller provides the label/header; the aria mirror keeps a11y/tests. */
  fill = false,
}: {
  label: string;
  hint?: string;
  value: string;
  onChange: (next: string) => void;
  ambientDts?: string;
  placeholder?: string;
  language?: "javascript" | "typescript";
  onDiagnostics?: (errorCount: number) => void;
  fill?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  useEffect(ensureJsxConfigured, []);

  const editor = (height: string, inline = false) => (
    <CodeEditor
      language={language}
      value={value || ""}
      onChange={onChange}
      height={height}
      inline={inline}
      jsAmbientTypes={ambientDts}
      onMarkers={onDiagnostics}
    />
  );

  const expandDialog = (
    <Dialog open={expanded} onOpenChange={setExpanded}>
      <DialogContent className="flex flex-col p-0 sm:!h-[90vh] sm:!max-h-[90vh] sm:!w-[min(96vw,1400px)] sm:!max-w-[min(96vw,1400px)]">
        <DialogHeader className="border-b border-border px-4 py-2">
          <DialogTitle className="text-sm">{label}</DialogTitle>
        </DialogHeader>
        <DialogBody className="min-h-0 flex-1 p-0">
          <div className="h-full">{editor("100%")}</div>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );

  if (fill) {
    // HERO: the editor grows to fill its flex parent (the query editor's main pane).
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        <div className="relative min-h-0 flex-1 overflow-hidden rounded-md border border-border focus-within:border-accent">
          <textarea aria-label={label} value={value || ""} readOnly hidden />
          {value ? null : (
            <div className="pointer-events-none absolute left-3 top-2 z-[1] whitespace-pre font-mono text-xs text-muted-foreground/50">
              {placeholder}
            </div>
          )}
          <div className="h-full">{editor("100%")}</div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="absolute right-1.5 top-1.5 z-10 h-7 w-7 bg-surface/80 backdrop-blur hover:bg-surface"
            title="Espandi editor"
            aria-label="Espandi editor"
            onClick={() => setExpanded(true)}
          >
            <Maximize2 className="h-4 w-4" />
          </Button>
        </div>
        {expandDialog}
      </div>
    );
  }

  return (
    <Field label={label} layout="stack" hint={hint}>
      <div className="relative overflow-hidden rounded border border-border focus-within:border-accent">
        {/* aria textarea mirror so existing tests / a11y can read the value */}
        <textarea aria-label={label} value={value || ""} readOnly hidden />
        {value ? null : (
          <div className="pointer-events-none absolute left-3 top-2 z-[1] whitespace-pre font-mono text-xs text-muted-foreground/50">
            {placeholder}
          </div>
        )}
        <div className="h-[200px]">{editor("100%", true)}</div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="absolute right-1.5 top-1.5 z-10 h-7 w-7 bg-surface/80 backdrop-blur hover:bg-surface"
          title="Espandi editor"
          aria-label="Espandi editor"
          onClick={() => setExpanded(true)}
        >
          <Maximize2 className="h-4 w-4" />
        </Button>
      </div>

      <Dialog open={expanded} onOpenChange={setExpanded}>
        <DialogContent className="flex flex-col p-0 sm:!h-[90vh] sm:!max-h-[90vh] sm:!w-[min(96vw,1400px)] sm:!max-w-[min(96vw,1400px)]">
          <DialogHeader className="border-b border-border px-4 py-2">
            <DialogTitle className="text-sm">{label}</DialogTitle>
          </DialogHeader>
          <DialogBody className="min-h-0 flex-1 p-0">
            <div className="h-full">{editor("100%")}</div>
          </DialogBody>
        </DialogContent>
      </Dialog>
    </Field>
  );
}
